import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import CampaignLandingPage from './CampaignLandingPage';
import LottieLoader from '../../Components/common/LottieLoader';
import { LandingPageContainer, BackgroundContainer, LandingPageWrapper, Logo, ContentCard, Title, MessageText, NextButton } from './CampaignLandingPageStyle';

// /campaign/:slug/:code (2026-10-22): one address for every study. A voice study
// mounts the call landing page exactly as before; a web_form study mounts the form
// player. The study is read once here (the public link endpoint); the landing
// page keeps its own fetch, so nothing about it changes.
const FormPlayerPage = lazy(() => import('../FormPlayer/FormPlayerPage'));

const Waiting = ({ title = 'Processing...', note = 'Loading survey...' }) => (
  <LandingPageContainer>
    <BackgroundContainer />
    <LandingPageWrapper>
      <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
      <ContentCard>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <LottieLoader inline size="medium" />
          <Title style={{ marginTop: 24, marginBottom: 8 }}>{title}</Title>
          <MessageText style={{ marginBottom: 0 }}>{note}</MessageText>
        </div>
      </ContentCard>
    </LandingPageWrapper>
  </LandingPageContainer>
);

/** Voice or text (Phase 5): the chooser a respondent-choice study shows first. */
function ModalityChooser({ campaign, transactionId, onChosen }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const choose = async (modality) => {
    setBusy(modality); setError(null);
    try {
      await axios.post(`${config.api.base_url}/survey-respondents/${encodeURIComponent(transactionId)}/modality`, { modality });
      onChosen(modality);
    } catch (e) {
      setError(e.response?.data?.message || 'Please try again.');
      setBusy(null);
    }
  };
  const name = campaign?.topic || campaign?.campaign_name || 'This study';
  return (
    <LandingPageContainer>
      <BackgroundContainer />
      <LandingPageWrapper>
        <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
        <ContentCard>
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <Title>{name}</Title>
            <MessageText style={{ color: '#666', lineHeight: 1.6 }}>How would you like to take part? You can talk it through with our interviewer, or answer on screen at your own pace. Both take about the same time.</MessageText>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
              <NextButton onClick={() => choose('voice_quant')} disabled={!!busy}>{busy === 'voice_quant' ? 'One moment...' : 'Talk it through'}</NextButton>
              <NextButton onClick={() => choose('web_form')} disabled={!!busy} style={{ background: '#fff', color: '#2E45DA', border: '1.5px solid #2E45DA' }}>{busy === 'web_form' ? 'One moment...' : 'Answer on screen'}</NextButton>
            </div>
            {error && <MessageText style={{ color: '#B02E0E', marginTop: 14 }}>{error}</MessageText>}
          </div>
        </ContentCard>
      </LandingPageWrapper>
    </LandingPageContainer>
  );
}

/** The study's open link was refused (turned off, expired, closed, not open yet). */
const Refused = ({ reason, message }) => {
  const titles = { off: 'Link Turned Off', expired: 'Link Expired', invalid: 'Invalid Link', study_closed: 'Survey Closed', not_available: 'Survey Not Open Yet', busy: 'Please Try Again', error: 'Please Try Again' };
  return (
    <LandingPageContainer>
      <BackgroundContainer />
      <LandingPageWrapper>
        <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
        <ContentCard>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Title style={{ marginBottom: 16 }}>{titles[reason] || 'Survey Not Available'}</Title>
            <MessageText style={{ color: '#666', lineHeight: 1.6, marginBottom: 0 }}>{message || 'This survey is not available.'}</MessageText>
          </div>
        </ContentCard>
      </LandingPageWrapper>
    </LandingPageContainer>
  );
};

export default function CampaignEntryGate() {
  const { slug, code } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionId = searchParams.get('transaction_id');
  const [state, setState] = useState({ phase: 'loading', campaign: null });
  // A form study's open link (?ol=<token>): this browser asks for its own form
  // session, the same one until that person has submitted, exactly as the call
  // page does for a voice study (which keeps its own join).
  const openLinkToken = searchParams.get('ol');
  const [openLink, setOpenLink] = useState({ status: openLinkToken ? 'joining' : 'none', refusal: null });
  const openLinkStartedRef = useRef(false);
  // Respondent-choice studies: what this respondent picked (null until they do).
  const [chosen, setChosen] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${config.api.base_url}/campaigns/link/${encodeURIComponent(slug)}/${encodeURIComponent(code)}`);
        const campaign = res.data?.data || res.data || null;
        if (!cancelled) setState({ phase: 'ready', campaign });
      } catch (e) {
        // Unknown link or a transient failure: the landing page shows its own
        // "Study Not Found" / retry states, so hand over to it.
        if (!cancelled) setState({ phase: 'ready', campaign: null });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, code]);

  useEffect(() => {
    if (!openLinkToken || transactionId || openLinkStartedRef.current) return;
    // A form study and a respondent-choice study join here; a voice study's call page keeps its own join.
    if (!state.campaign || !['web_form', 'respondent_choice'].includes(state.campaign.modality)) return;
    openLinkStartedRef.current = true;
    const storageKey = `cv_open_link_${slug}_${code}`;
    let known = null;
    try { known = window.localStorage.getItem(storageKey); } catch (e) { known = null; }
    axios.post(`${config.api.base_url}/survey-respondents/open-link/join`, { token: openLinkToken, slug, code, transaction_id: known || undefined }, { timeout: 20000 })
      .then((res) => {
        const tx = res.data?.data?.transaction_id;
        if (!tx) {
          setOpenLink({ status: 'refused', refusal: { reason: res.data?.data?.reason || 'error', message: res.data?.message } });
          return;
        }
        try { window.localStorage.setItem(storageKey, tx); } catch (e) { /* private mode: a new session next time */ }
        setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('transaction_id', tx); return next; }, { replace: true });
        setOpenLink({ status: 'done', refusal: null });
      })
      .catch((err) => {
        const body = err?.response?.data || {};
        const reason = body.data?.reason || (err?.response?.status === 429 ? 'busy' : 'error');
        setOpenLink({ status: 'refused', refusal: { reason, message: body.message || 'We could not open this survey. Please check your connection and try again.' } });
      });
  }, [openLinkToken, transactionId, state.campaign, slug, code, setSearchParams]);

  useEffect(() => {
    if (!state.campaign || state.campaign.modality !== 'respondent_choice' || !transactionId) return undefined;
    let cancelled = false;
    axios.get(`${config.api.base_url}/survey-respondents/${encodeURIComponent(transactionId)}`)
      .then((res) => { if (cancelled) return; const m = res.data?.data?.modality; setChosen(m === 'web_form' || m === 'voice_quant' || m === 'voice_interview' ? m : null); })
      .catch(() => { if (!cancelled) setChosen(null); });
    return () => { cancelled = true; };
  }, [state.campaign, transactionId]);

  if (state.phase === 'loading') return <Waiting />;
  if (state.campaign && state.campaign.modality === 'respondent_choice' && openLinkToken && !transactionId) {
    if (openLink.status === 'refused') return <Refused reason={openLink.refusal?.reason} message={openLink.refusal?.message} />;
    return <Waiting note="Opening your study" />;
  }
  if (state.campaign && state.campaign.modality === 'respondent_choice' && transactionId) {
    if (chosen === undefined) return <Waiting />;
    if (chosen === null) return <ModalityChooser campaign={state.campaign} transactionId={transactionId} onChosen={setChosen} />;
    if (chosen === 'web_form') {
      return (
        <Suspense fallback={<Waiting note="Preparing your questions" />}>
          <FormPlayerPage campaign={state.campaign} />
        </Suspense>
      );
    }
    return <CampaignLandingPage />;
  }
  if (state.campaign && state.campaign.modality === 'web_form') {
    if (openLinkToken && !transactionId) {
      if (openLink.status === 'refused') return <Refused reason={openLink.refusal?.reason} message={openLink.refusal?.message} />;
      return <Waiting note="Opening your survey" />;
    }
    return (
      <Suspense fallback={<Waiting note="Preparing your questions" />}>
        <FormPlayerPage campaign={state.campaign} />
      </Suspense>
    );
  }
  return <CampaignLandingPage />;
}
